import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Clock, FileDown, FileText, GraduationCap, Info, Play, Send, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { useAuth } from "@/hooks/useAuth";
import { Fragen } from "@/components/bericht/Fragen";
import type {
  Befund,
  Bericht,
  BerichtJetzt,
  BerichtBild,
  BerichtRaster,
  GenericResponse,
  MusterEinstufung,
  Schwere,
} from "@/lib/types";

/** Auswahl für „Jetzt erzeugen". Mehr Zeiträume braucht niemand von Hand. */
const ZEITRAEUME: { stunden: number; label: string }[] = [
  { stunden: 8, label: t("letzte Schicht (8 h)") },
  { stunden: 24, label: t("24 Stunden") },
  { stunden: 24 * 7, label: t("7 Tage") },
];

const feld =
  "h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary";
const knopfKlein =
  "inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium hover:bg-muted disabled:opacity-60";

function zeit(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SchwereBadge({ schwere }: { schwere: Schwere }) {
  const text =
    schwere === "dringend" ? t("Dringend") : schwere === "warnung" ? t("Warnung") : t("Hinweis");
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
        schwere === "dringend" && "bg-destructive/15 text-destructive",
        schwere === "warnung" && "bg-warning/15 text-warning",
        schwere === "hinweis" && "bg-muted text-muted-foreground",
      )}
    >
      {text}
    </span>
  );
}

/**
 * Die Antwort des Menschen auf ein gefundenes Muster. Der Bericht findet,
 * dass sich etwas jeden Tag wiederholt; ob das eine Pause ist oder ein
 * Problem, weiß nur, wer die Anlage kennt.
 */
function Einstufen({ b, onErledigt }: { b: Befund; onErledigt: (text: string) => void }) {
  const [grund, setGrund] = useState("");
  const [stummBis, setStummBis] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const sende = async (status: "normal" | "unnormal" | "stumm") => {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api.post<GenericResponse>("/api/bericht/muster", {
        schluessel: b.schluessel,
        status,
        grund,
        beschreibung: b.titel,
        stumm_bis: status === "stumm" ? stummBis : null,
      });
      if (r.success) onErledigt(r.message);
      else setFehler(r.message);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t("Speichern fehlgeschlagen"));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="mt-1 flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          placeholder={t("Stichwort, z. B. Pause")}
          maxLength={80}
          className={cn(feld, "w-48")}
        />
        <button type="button" disabled={laeuft} onClick={() => void sende("normal")} className={knopfKlein}>
          <Check className="h-3.5 w-3.5" /> {t("Normal")}
        </button>
        <button type="button" disabled={laeuft} onClick={() => void sende("unnormal")} className={knopfKlein}>
          <AlertTriangle className="h-3.5 w-3.5" /> {t("Nicht normal")}
        </button>
        <span className="text-[11px] text-muted-foreground">{t("oder stumm bis")}</span>
        <input
          type="date"
          value={stummBis}
          onChange={(e) => setStummBis(e.target.value)}
          className={feld}
        />
        <button
          type="button"
          disabled={laeuft || !stummBis}
          onClick={() => void sende("stumm")}
          className={knopfKlein}
        >
          {t("Stumm schalten")}
        </button>
      </div>
      {fehler ? <div className="text-[11px] text-destructive">{fehler}</div> : null}
    </div>
  );
}

function BefundZeile({
  b,
  darfEinstufen,
  onErledigt,
}: {
  b: Befund;
  darfEinstufen: boolean;
  onErledigt: (schluessel: string, text: string) => void;
}) {
  return (
    <li className="flex flex-col gap-1 border-b border-border py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <SchwereBadge schwere={b.schwere} />
        <span className="text-sm font-medium">{b.titel}</span>
        {b.seit ? (
          <span className="text-[11px] text-muted-foreground">{t("seit {tag}", { tag: b.seit })}</span>
        ) : (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
            {t("neu")}
          </span>
        )}
        {b.vorlaeufig ? (
          <span
            className="rounded-full border border-dashed border-border px-2 py-0.5 text-[10px] text-muted-foreground"
            title={t("Es liegen noch weniger Tage vor, als für ein belastbares Urteil nötig sind.")}
          >
            {t("vorläufig")}
          </span>
        ) : null}
      </div>
      {/* Die Zahl steht immer mit ihrem Maßstab da: ohne Vergleichswert kann
          niemand entscheiden, ob sie viel ist. */}
      <div className="font-mono text-xs text-foreground/90">{b.zahl}</div>
      <div className="text-xs text-muted-foreground">{b.handlung}</div>
      {b.frage && darfEinstufen ? (
        <Einstufen b={b} onErledigt={(text) => onErledigt(b.schluessel, text)} />
      ) : null}
      {b.link ? (
        <a href={b.link} className="w-fit text-xs text-primary underline-offset-2 hover:underline">
          {t("Dazu die Logs ansehen")}
        </a>
      ) : null}
    </li>
  );
}

/**
 * Raster Stunde mal Tag. Ein Loch, das sich jeden Tag zur selben Zeit
 * wiederholt, sieht man hier sofort, und beim Einstufen weiß man, worüber
 * man entscheidet.
 */
function Raster({ clients }: { clients: string[] }) {
  const [client, setClient] = useState(clients[0] ?? "");
  const [groesse, setGroesse] = useState<BerichtRaster["groesse"]>("zyklen");
  const [daten, setDaten] = useState<BerichtRaster | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!client) return;
    let aktiv = true;
    setFehler(null);
    api
      .get<BerichtRaster>(
        `/api/bericht/raster?client=${encodeURIComponent(client)}&tage=14&groesse=${groesse}`,
      )
      .then((r) => {
        if (aktiv) setDaten(r);
      })
      .catch((e) => {
        if (aktiv) setFehler(e instanceof Error ? e.message : t("Laden fehlgeschlagen"));
      });
    return () => {
      aktiv = false;
    };
  }, [client, groesse]);

  const max = useMemo(
    () => Math.max(1, ...(daten?.tage ?? []).flatMap((z) => z.werte.map((w) => w ?? 0))),
    [daten],
  );
  const name = (g: BerichtRaster["groesse"]) =>
    g === "zyklen" ? t("Ausstoß") : g === "meldungen" ? t("Meldungen") : t("Fehler");

  if (clients.length === 0) return null;

  return (
    <Card
      title={t("Verlauf je Stunde")}
      description={t("Je Tag eine Zeile, je Stunde ein Feld. Je kräftiger, desto mehr.")}
      actions={
        <>
          <select value={client} onChange={(e) => setClient(e.target.value)} className={cn(feld, "cursor-pointer")}>
            {clients.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={groesse}
            onChange={(e) => setGroesse(e.target.value as BerichtRaster["groesse"])}
            className={cn(feld, "cursor-pointer")}
          >
            <option value="zyklen">{t("Ausstoß")}</option>
            <option value="meldungen">{t("Meldungen")}</option>
            <option value="fehler">{t("Fehler")}</option>
          </select>
        </>
      }
    >
      {fehler ? (
        <div className="text-xs text-destructive">{fehler}</div>
      ) : !daten ? (
        <div className="text-xs text-muted-foreground">{t("Lade…")}</div>
      ) : (
        <div className="flex flex-col gap-2">
          {daten.groesse !== groesse ? (
            <p className="text-[11px] text-muted-foreground">
              {t("Diese Anlage schickt keine Stundenbilanz. Gezeigt werden stattdessen die Meldungen.")}
            </p>
          ) : null}
          <div className="overflow-x-auto">
            <div className="inline-grid gap-px" style={{ gridTemplateColumns: "auto repeat(24, 1.1rem)" }}>
              <div />
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="text-center text-[9px] text-muted-foreground">
                  {h % 3 === 0 ? h : ""}
                </div>
              ))}
              {daten.tage.map((z) => (
                <RasterZeile key={z.tag} zeile={z} max={max} einheit={name(daten.groesse)} />
              ))}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t("Leere Felder: für diese Stunde liegt nichts vor. Wochenenden sind blasser beschriftet.")}
          </p>
        </div>
      )}
    </Card>
  );
}

/** Runde Obergrenze und Schrittweite einer Achse ab null (wie im PDF). */
function achse(max: number): [number, number] {
  if (!(max > 0)) return [1, 1];
  const roh = max / 4;
  const potenz = Math.max(1, 10 ** Math.floor(Math.log10(roh)));
  const schritt = [1, 2, 5, 10].map((m) => m * potenz).find((s) => s >= roh) ?? 10 * potenz;
  return [Math.ceil(max / schritt) * schritt, schritt];
}

/**
 * Der Zeitraum Stunde für Stunde: Balken für das, was war, ein Strich für
 * das, was zu dieser Stunde üblich ist. Dasselbe Bild wie im PDF.
 */
function StundenBild({ bild }: { bild: BerichtBild }) {
  const n = bild.stunden.length;
  const [obergrenze, schritt] = achse(
    Math.max(0, ...bild.stunden.flatMap((p) => [p.ist ?? 0, p.ueblich ?? 0])),
  );
  const B = 720;
  const H = 190;
  const links = 34;
  const oben = 10;
  const unten = H - 34;
  const slot = (B - links) / n;
  const balken = Math.min(22, Math.max(2, slot * 0.64));
  const y = (w: number) => unten - (Math.min(w, obergrenze) / obergrenze) * (unten - oben);
  const striche = Array.from({ length: Math.round(obergrenze / schritt) + 1 }, (_, i) => i * schritt);
  const wort = bild.groesse === "zyklen" ? t("Ausstoß") : bild.groesse === "meldungen" ? t("Meldungen") : t("Fehler");
  // Ein Strich bei null läge auf der Achse und sagte nichts (wie im PDF).
  const hatUeblich = bild.stunden.some((p) => (p.ueblich ?? 0) > 0);
  const ueblichNull = !hatUeblich && bild.stunden.some((p) => p.ueblich != null);
  const hatAuffaellig = bild.stunden.some((p) => p.auffaellig);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span className="text-xs font-medium text-foreground">{bild.client}</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-[2px] bg-primary" /> {wort}
        </span>
        {hatAuffaellig ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-[2px] bg-destructive" /> {t("auffällig")}
          </span>
        ) : null}
        {hatUeblich ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-3.5 bg-foreground" /> {t("üblich zu dieser Stunde (Median der Vortage)")}
          </span>
        ) : ueblichNull ? (
          <span>{t("üblich zu diesen Stunden: 0 (Median der Vortage)")}</span>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${B} ${H}`} className="w-full min-w-[34rem] max-w-3xl" role="img" aria-label={`${bild.client}: ${wort}`}>
          {striche.map((w) => (
            <g key={w}>
              <line x1={links} x2={B} y1={y(w)} y2={y(w)} className={w === 0 ? "stroke-muted-foreground/50" : "stroke-border"} strokeWidth={1} />
              <text x={links - 6} y={y(w) + 3} textAnchor="end" className="fill-muted-foreground text-[10px]">
                {w.toLocaleString("de-DE")}
              </text>
            </g>
          ))}
          {bild.stunden.map((p, i) => {
            const x = links + i * slot;
            const mitte = x + slot / 2;
            const neuerTag = i === 0 || bild.stunden[i - 1].tag !== p.tag;
            const hoehe = p.ist != null && p.ist > 0 ? Math.max(1, unten - y(p.ist)) : 0;
            return (
              <g key={`${p.tag}-${p.stunde}`}>
                <title>
                  {`${p.tag} ${p.stunde} ${t("Uhr")}: ${p.ist == null ? t("keine Daten") : Math.round(p.ist)}` +
                    (p.ueblich != null ? `, ${t("üblich")} ${Math.round(p.ueblich)}` : "")}
                </title>
                {/* Unsichtbare Fläche, damit der Hinweis auch über leeren Stunden erscheint. */}
                <rect x={x} y={oben} width={slot} height={unten - oben} fill="transparent" />
                {neuerTag && i > 0 ? <line x1={x} x2={x} y1={oben} y2={unten + 26} className="stroke-border" strokeWidth={1} /> : null}
                {neuerTag ? (
                  <text x={x + (i > 0 ? 4 : 0)} y={unten + 26} className="fill-muted-foreground text-[10px] font-medium">
                    {p.tag}
                  </text>
                ) : null}
                {hoehe > 0 ? (
                  <rect
                    x={mitte - balken / 2}
                    y={unten - hoehe}
                    width={balken}
                    height={hoehe}
                    rx={2}
                    className={p.auffaellig ? "fill-destructive" : "fill-primary"}
                  />
                ) : null}
                {p.auffaellig ? (
                  <text x={mitte} y={unten - hoehe - 4} textAnchor="middle" className="fill-destructive text-[10px] font-semibold">
                    {Math.round(p.ist ?? 0)}
                  </text>
                ) : null}
                {p.ueblich != null && p.ueblich > 0 ? (
                  <>
                    <line x1={x + slot * 0.08} x2={x + slot * 0.92} y1={y(p.ueblich)} y2={y(p.ueblich)} className="stroke-card" strokeWidth={4} />
                    <line x1={x + slot * 0.08} x2={x + slot * 0.92} y1={y(p.ueblich)} y2={y(p.ueblich)} className="stroke-foreground" strokeWidth={2} />
                  </>
                ) : null}
                <text x={mitte} y={unten + 13} textAnchor="middle" className="fill-muted-foreground text-[10px]">
                  {String(p.stunde).padStart(2, "0")}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function RasterZeile({
  zeile,
  max,
  einheit,
}: {
  zeile: BerichtRaster["tage"][number];
  max: number;
  einheit: string;
}) {
  const [, monat, tag] = zeile.tag.split("-");
  return (
    <>
      <div
        className={cn(
          "pr-2 text-right font-mono text-[10px] leading-[1.1rem]",
          zeile.werktag ? "text-foreground/80" : "text-muted-foreground/60",
        )}
      >
        {tag}.{monat}.
      </div>
      {zeile.werte.map((w, h) => (
        <div
          key={h}
          title={
            w === null
              ? `${tag}.${monat}. ${h} ${t("Uhr")}: ${t("keine Daten")}`
              : `${tag}.${monat}. ${h} ${t("Uhr")}: ${Math.round(w)} ${einheit}`
          }
          className={cn("h-[1.1rem] rounded-[2px]", w === null && "border border-dashed border-border/60")}
          style={
            w === null
              ? undefined
              : // Null ist ein Wert (die Anlage lief nicht), kein fehlendes
                // Feld: sichtbar, aber fast leer.
                { backgroundColor: `hsl(var(--primary) / ${0.08 + 0.92 * (w / max)})` }
          }
        />
      ))}
    </>
  );
}

function EingestufteMuster({
  stand,
  darfAendern,
  onGeaendert,
}: {
  stand: number;
  darfAendern: boolean;
  onGeaendert: (text: string) => void;
}) {
  const [liste, setListe] = useState<MusterEinstufung[]>([]);

  useEffect(() => {
    let aktiv = true;
    api
      .get<{ muster: MusterEinstufung[] }>("/api/bericht/muster")
      .then((r) => {
        if (aktiv) setListe(r.muster ?? []);
      })
      .catch(() => {
        /* Die Liste ist Beiwerk: Ohne sie funktioniert der Bericht trotzdem. */
      });
    return () => {
      aktiv = false;
    };
  }, [stand]);

  const zurueck = async (m: MusterEinstufung) => {
    try {
      const r = await api.post<GenericResponse>("/api/bericht/muster", {
        schluessel: m.schluessel,
        status: "offen",
      });
      onGeaendert(r.message);
    } catch (e) {
      onGeaendert(e instanceof Error ? e.message : t("Speichern fehlgeschlagen"));
    }
  };

  if (liste.length === 0) return null;
  const wort = (m: MusterEinstufung) =>
    m.status === "normal" ? t("normal") : m.status === "unnormal" ? t("nicht normal") : t("stumm");

  return (
    <Card
      title={t("Eingestufte Muster")}
      description={t("Was Sie dem Bericht schon gesagt haben. Eine Einstufung als normal wird nach einem halben Jahr noch einmal vorgelegt.")}
    >
      <ul className="flex flex-col">
        {liste.map((m) => (
          <li
            key={m.schluessel}
            className="flex flex-wrap items-center gap-2 border-b border-border py-2 text-xs last:border-0"
          >
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-medium",
                m.status === "unnormal" ? "bg-warning/15 text-warning" : "bg-muted text-muted-foreground",
              )}
            >
              {wort(m)}
            </span>
            <span className="font-medium">{m.grund || t("(ohne Stichwort)")}</span>
            <span className="text-muted-foreground">{m.beschreibung || m.schluessel}</span>
            <span className="text-[11px] text-muted-foreground">
              {m.status === "stumm" && m.stumm_bis
                ? t("bis {tag}", { tag: m.stumm_bis })
                : t("seit {tag}", { tag: m.entschieden })}
            </span>
            {!m.gilt ? (
              <span className="text-[11px] text-warning">{t("abgelaufen, wird wieder vorgelegt")}</span>
            ) : null}
            {darfAendern ? (
              <button type="button" onClick={() => void zurueck(m)} className={cn(knopfKlein, "ml-auto h-7")}>
                <Undo2 className="h-3 w-3" /> {t("Zurücknehmen")}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function BerichtAnsicht({
  bericht,
  erledigt,
  darfEinstufen,
  onErledigt,
}: {
  bericht: Bericht;
  erledigt: Set<string>;
  darfEinstufen: boolean;
  onErledigt: (schluessel: string, text: string) => void;
}) {
  // Eben eingestufte Muster verschwinden sofort aus der Liste, ohne dass der
  // Bericht neu gerechnet werden muss.
  const befunde = bericht.befunde.filter((b) => !erledigt.has(b.schluessel));
  const neu = befunde.filter((b) => !b.seit).length;
  const bekannt = bericht.bekannt ?? [];
  const lernstand = bericht.lernstand ?? [];
  // Nur Anlagen, für die es Stunden zu zeigen gibt (bei Wochenberichten
  // bleibt es beim Raster weiter unten).
  const bilder = (bericht.bilder ?? []).filter((b) => b.stunden.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <Card
        title={
          befunde.length === 0
            ? t("Nichts Auffälliges")
            : t("{n} Auffälligkeiten, davon {neu} neu", { n: befunde.length, neu })
        }
        description={t("Zeitraum {von} bis {bis}", {
          von: zeit(bericht.von),
          bis: zeit(bericht.bis),
        })}
      >
        {befunde.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("Im Zeitraum war nichts ungewöhnlich. Genau dafür ist der Bericht da: Stille ist die gute Nachricht.")}
          </p>
        ) : (
          <ul className="flex flex-col">
            {befunde.map((b) => (
              <BefundZeile key={b.schluessel} b={b} darfEinstufen={darfEinstufen} onErledigt={onErledigt} />
            ))}
          </ul>
        )}
      </Card>

      {bericht.einordnung ? (
        <Card title={t("Einordnung")}>
          <p className="whitespace-pre-wrap text-sm">{bericht.einordnung}</p>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {t("Dieser Absatz ist automatisch formuliert. Die Zahlen oben stammen aus der Datenbank, nicht aus dem Sprachmodell.")}
          </p>
        </Card>
      ) : null}

      {bilder.length > 0 ? (
        <Card
          title={t("Verlauf im Zeitraum")}
          description={t("Stunde für Stunde, verglichen mit derselben Stunde an den Vortagen.")}
        >
          <div className="flex flex-col gap-5">
            {bilder.map((bild) => (
              <StundenBild key={bild.client} bild={bild} />
            ))}
          </div>
        </Card>
      ) : null}

      {lernstand.length > 0 ? (
        <Card
          title={t("Lernphase")}
          description={t("Aussagen über Uhrzeiten brauchen Vortage. Geplante Berichte warten, bis genug da sind; auf Knopfdruck sehen Sie den vorläufigen Stand.")}
        >
          <ul className="flex flex-col gap-2">
            {lernstand.map((l) => (
              <li key={l.client} className="flex items-center gap-3 text-xs">
                <GraduationCap className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="w-32 shrink-0 truncate font-mono">{l.client}</span>
                <div className="h-1.5 w-40 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary"
                    style={{ width: `${Math.min(100, (l.tage / Math.max(1, l.noetig)) * 100)}%` }}
                  />
                </div>
                <span className="text-muted-foreground">
                  {t("Tag {tage} von {noetig}", { tage: l.tage, noetig: l.noetig })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {bekannt.length > 0 ? (
        <Card title={t("Bekannt und normal")} description={t("Als normal eingestuft und im Zeitraum wie gewohnt aufgetreten.")}>
          <ul className="flex flex-col gap-1 text-xs">
            {bekannt.map((k) => (
              <li key={k.schluessel} className="flex flex-wrap gap-2">
                <span className="font-mono">{k.client}</span>
                {k.grund ? <span className="font-medium">{k.grund}</span> : null}
                <span className="text-muted-foreground">{k.text}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {bericht.zeilen.length > 0 ? (
        <Card title={t("Anlagen im Zeitraum")}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-1.5 pr-3 font-medium">{t("Anlage")}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t("Meldungen")}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t("Fehler")}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t("Warnungen")}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t("Störfälle")}</th>
                  <th className="py-1.5 font-medium">{t("Kennzahlen")}</th>
                </tr>
              </thead>
              <tbody>
                {bericht.zeilen.map((z) => (
                  <tr key={z.client} className="border-b border-border last:border-0">
                    <td className="py-1.5 pr-3 font-mono">{z.client}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{z.logs}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{z.fehler}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{z.warnungen}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{z.stoerfaelle}</td>
                    <td className="py-1.5 text-muted-foreground">
                      {z.kennzahlen.map(([k, v]) => `${k}: ${v}`).join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <Raster clients={bericht.zeilen.map((z) => z.client)} />

      {bericht.hinweise.length > 0 ? (
        <Card title={t("Einschränkungen")}>
          <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
            {bericht.hinweise.map((h) => (
              <li key={h} className="flex gap-2">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

export function BerichtePage() {
  const { isAdmin } = useAuth();
  const [bericht, setBericht] = useState<Bericht | null>(null);
  const [geplant, setGeplant] = useState(true);
  const [stunden, setStunden] = useState(24);
  const [laeuft, setLaeuft] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [erledigt, setErledigt] = useState<Set<string>>(new Set());
  // Zählt hoch, wenn sich an den Einstufungen etwas geändert hat: Die Liste
  // darunter lädt sich dann neu.
  const [musterStand, setMusterStand] = useState(0);

  const letzten = useCallback(async () => {
    try {
      const b = await api.get<Bericht | null>("/api/bericht/letzter");
      setBericht(b);
      setGeplant(true);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    }
  }, []);

  useEffect(() => {
    void letzten();
  }, [letzten]);

  const erzeugen = async (senden: boolean) => {
    setLaeuft(true);
    setFeedback(null);
    try {
      const r = await api.post<BerichtJetzt>("/api/bericht/jetzt", { stunden, senden });
      setBericht(r.bericht);
      setErledigt(new Set());
      setGeplant(false);
      if (senden) {
        setFeedback({
          kind: r.gesendet ? "success" : "error",
          text: r.gesendet
            ? t("Bericht versendet.")
            : t("Nichts versendet: kein Kanal gewählt oder Versand fehlgeschlagen. Siehe Einstellungen, Bericht."),
        });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Erzeugen fehlgeschlagen") });
    } finally {
      setLaeuft(false);
    }
  };

  const eingestuft = (schluessel: string, text: string) => {
    setErledigt((cur) => new Set([...cur, schluessel]));
    setMusterStand((n) => n + 1);
    setFeedback({ kind: "success", text });
  };

  const knopf =
    "inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium disabled:opacity-60";

  return (
    <div>
      <PageHeader
        title={t("Berichte")}
        description={t("Was im Zeitraum auffällig war, in Sätzen statt in Logzeilen.")}
        actions={
          isAdmin ? (
            <>
              <select
                value={stunden}
                onChange={(e) => setStunden(Number(e.target.value))}
                className="h-9 cursor-pointer rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary"
              >
                {ZEITRAEUME.map((z) => (
                  <option key={z.stunden} value={z.stunden}>
                    {z.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={laeuft}
                onClick={() => void erzeugen(false)}
                className={cn(knopf, "border border-border bg-card hover:bg-muted")}
              >
                <Play className="h-3.5 w-3.5" /> {laeuft ? t("Rechne…") : t("Jetzt erzeugen")}
              </button>
              <button
                type="button"
                disabled={laeuft}
                onClick={() => void erzeugen(true)}
                className={cn(knopf, "bg-primary text-primary-foreground")}
              >
                <Send className="h-3.5 w-3.5" /> {t("Erzeugen und senden")}
              </button>
            </>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 p-3 md:p-6">
        <FormMessage feedback={feedback} />

        {bericht ? (
          <>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {geplant ? <Clock className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
              <span>
                {geplant
                  ? t("Zuletzt geplant erzeugt am {zeit}", { zeit: zeit(bericht.erzeugt) })
                  : t("Soeben auf Knopfdruck erzeugt")}
              </span>
              {bericht.id ? (
                <a
                  href={`/api/bericht/pdf/${bericht.id}.pdf`}
                  target="_blank"
                  rel="noopener"
                  className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                  <FileDown className="h-3.5 w-3.5" /> {t("Als PDF")}
                </a>
              ) : null}
            </div>
            <BerichtAnsicht
              bericht={bericht}
              erledigt={erledigt}
              darfEinstufen={isAdmin}
              onErledigt={eingestuft}
            />
          </>
        ) : (
          <Card title={t("Noch kein Bericht")}>
            <div className="flex gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                {isAdmin
                  ? t("Es ist noch kein geplanter Bericht gelaufen. Oben einen Zeitraum wählen und „Jetzt erzeugen“ drücken, oder unter Einstellungen, Bericht den Zeitplan einschalten.")
                  : t("Es ist noch kein Bericht gelaufen.")}
              </p>
            </div>
          </Card>
        )}

        <Fragen berichtId={bericht?.id} isAdmin={isAdmin} />

        <EingestufteMuster
          stand={musterStand}
          darfAendern={isAdmin}
          onGeaendert={(text) => {
            setMusterStand((n) => n + 1);
            setFeedback({ kind: "success", text });
          }}
        />
      </div>
    </div>
  );
}
